import { Injectable } from '@angular/core';
import {
  AppMasterDBFacade,
  FactoryService,
  PersistentStorageService,
  InputAppClientDBFacade,
} from '@monorepo/core-data';
import { DateUtils } from '@monorepo/utils';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, ObservableInput, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { TInputAppState } from '../reducer';
import {
  ChangeShiftId,
  LoadShifts,
  LoadShiftsFailed,
  SelectShift,
  SelectShiftFailed,
  ShiftsActionTypes,
  ShiftsLoaded,
  SessionPlansNoAction,
  FetchSessionPlans,
  CheckSessionHoursStatus,
  UpdateSessionHoursStatus,
} from './shifts.actions';
import { AppNotificationUtilClass } from '@monorepo/services';
import { SessionPlan, SESSION_PLANS_DATA, TREQUEST_SESSION_PLANS_DATA } from '@monorepo/types';
import { EventUtil } from '@monorepo/utils';

// Mock Session Plans for if api fails in first attempt then this data will be used
// and then we will update it with the api data for every api call
const initialSessionPlans: SessionPlan[] = [
  {
    src_type: 'session_plan',
    schedule_name: 'mock_schedule',
    schedule_id: 'default',
    subject_key: 'bhg-knit',
    start_time: '00:01',
    end_time: '12:00',
    duration: 60,
    sessionblock: '1',
    subtype: 'WORK',
  },
  {
    src_type: 'session_plan',
    schedule_name: 'mock_schedule',
    schedule_id: 'default',
    subject_key: 'bhg-knit',
    start_time: '07:00',
    end_time: '23:59',
    duration: 60,
    sessionblock: '2',
    subtype: 'WORK',
  },
];

@Injectable({ providedIn: 'root' })
export class ShiftsEffects {
  constructor(
    private actions$: Actions,
    private persistentStorageService: PersistentStorageService,
    private appMasterDBFacade: AppMasterDBFacade,
    private factoryService: FactoryService,
    private inputAppClientDBFacade: InputAppClientDBFacade,
  ) {}

  loadShifts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShiftsActionTypes.LoadShifts),
      switchMap(() =>
        this.appMasterDBFacade.getSelectedStation().pipe(
          take(1),
          map((s) => s),
        ),
      ),
      switchMap((stationOpt) => this.appMasterDBFacade.getClientInfo().pipe(map((ci) => [stationOpt, ci] as const))),
      switchMap(([stationOpt, clientInfo]) => {
        if (stationOpt)
          return this.factoryService
            .getShifts(stationOpt.subject, clientInfo.subject_key, DateUtils.getCrntDate())
            .pipe(catchError((error) => of(null)));
        else return of(null);
      }),
      switchMap((shifts) =>
        shifts ? from(this.persistentStorageService.set('[SHIFT][RESPONSE]', shifts)) : of(undefined),
      ),
      switchMap(() => from(this.persistentStorageService.get('[SHIFT][RESPONSE]'))),
      map((shiftInfo) => {
        if (shiftInfo) return new ShiftsLoaded(shiftInfo);
        else return new LoadShiftsFailed();
      }),
    ),
  );

  shiftsLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShiftsActionTypes.ShiftsLoaded),
      switchMap((action: ShiftsLoaded) =>
        from(this.persistentStorageService.get('[SHIFT][USER_SETTING]')).pipe(
          map((as) => {
            const activeShift = as ? as : action.payload[0].id;
            return activeShift;
          }),
        ),
      ),
      switchMap((activeShiftId) =>
        from(this.persistentStorageService.set('[SHIFT][USER_SETTING]', activeShiftId)).pipe(
          map(() => {
            return new SelectShift(activeShiftId);
          }),
          catchError(() => {
            console.log('Shifts not found');
            return of(new SelectShiftFailed());
          }),
        ),
      ),
    ),
  );

  InitialiseAndFetchSessionPlans$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShiftsActionTypes.InitialiseAndFetchSessionPlans),
      switchMap(() =>
        this.appMasterDBFacade.getAllStations().pipe(
          take(1),
          map((stations) => stations),
        ),
      ),
      switchMap((stations) =>
        this.appMasterDBFacade.getClientInfo().pipe(map((client) => [stations, client.subject_key] as const)),
      ),
      switchMap(([stations, subjectKey]) => {
        if (stations) {
          const allSubjects = stations
            .map((station) => station.subject)
            .filter((subject): subject is string => !!subject);

          return from(this.persistentStorageService.get('[SESSION_PLANS][RESPONSE]')).pipe(
            switchMap((existingPlans) => {
              const updatedPlans = existingPlans || ({} as SESSION_PLANS_DATA);
              allSubjects.forEach((subject) => {
                if (!updatedPlans[subject]) {
                  const subjectPlans = initialSessionPlans.map((plan) => ({
                    ...plan,
                    subject_key: subjectKey,
                  }));
                  updatedPlans[subject] = subjectPlans;
                }
              });
              return from(this.persistentStorageService.set('[SESSION_PLANS][RESPONSE]', updatedPlans)).pipe(
                map(() => new FetchSessionPlans(true)),
              );
            }),
          );
        } else {
          return of(new FetchSessionPlans(true));
        }
      }),
    ),
  );

  FetchSessionPlans$ = createEffect(() =>
    this.actions$.pipe(
      ofType<FetchSessionPlans>(ShiftsActionTypes.FetchSessionPlans),
      switchMap((action) =>
        this.appMasterDBFacade.getAllStations().pipe(
          take(1),
          map((stations) => [stations, action] as const),
        ),
      ),
      switchMap(([stations, action]) =>
        this.appMasterDBFacade.getClientInfo().pipe(take(1), map((clientInfo) => [stations, clientInfo.subject_key, action] as const)),
      ),
      switchMap(([stations, subject_key, action]) =>
        this.appMasterDBFacade.getSelectedStation().pipe(take(1), map((selectedStation) => [stations, subject_key, selectedStation, action] as const)),
      ),
      switchMap(([stations, subject_key, selectedStation, action]) => {
        if (selectedStation?.subject && stations) {
          const selectedSubject: string = selectedStation.subject;
          const allSubjects = stations
            .map((station) => station.subject)
            .filter((subject): subject is string => !!subject);
          const payload: TREQUEST_SESSION_PLANS_DATA = {
            subjects: action.fetchAllSubjects ? allSubjects : [selectedSubject],
            subject_key,
            session_dates: DateUtils.getCrntDate(),
          };
          return this.factoryService.getSessionPlansBySubjects(payload).pipe(
            map((sessionPlans) => [sessionPlans, allSubjects] as const),
            catchError(() => of([null, allSubjects] as const)),
          );
        } else {
          return of([null, null] as const);
        }
      }),
      switchMap(([sessionPlans, allSubjects]) => {
        // Handle no data cases first (mostly api failure/ no internet)
        if (!sessionPlans || !allSubjects) {
          return of(new SessionPlansNoAction());
        }
        return from(this.persistentStorageService.get('[SESSION_PLANS][RESPONSE]')).pipe(
          switchMap((existingPlans) => {
            const updatedPlans = { ...existingPlans };
            sessionPlans.forEach(({ subject, session_plan_list }) => {
              updatedPlans[subject] = session_plan_list;
            });
            return from(this.persistentStorageService.set('[SESSION_PLANS][RESPONSE]', updatedPlans)).pipe(
              map(() => new SessionPlansNoAction()),
            );
          }),
        );
      }),
    ),
  );

  CheckSessionHoursStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckSessionHoursStatus>(ShiftsActionTypes.CheckSessionHoursStatus),
      switchMap(() =>
        this.inputAppClientDBFacade.getConfigurations().pipe(
          take(1),
          map((config) => config.sessionEndBufferTimeInMinutes),
        ),
      ),
      switchMap((sessionEndBufferTimeInMinutes) =>
        this.appMasterDBFacade.getSelectedStation().pipe(
          take(1),
          map((selectedStation) => [sessionEndBufferTimeInMinutes, selectedStation] as const),
        ),
      ),
      switchMap(([sessionEndBufferTimeInMinutes, selectedStation]) => {
        if (!selectedStation?.subject) {
          return of(new SessionPlansNoAction());
        }
        return from(this.persistentStorageService.get('[SESSION_PLANS][RESPONSE]')).pipe(
          map((sessionPlans) => {
            const subjectPlans = sessionPlans?.[selectedStation.subject];
            if (!subjectPlans || subjectPlans.length === 0) {
              return new UpdateSessionHoursStatus(true, {
                start_time: '',
                end_time: '',
              });
            }
            const firstPlan = subjectPlans[0];
            const lastPlan = subjectPlans[subjectPlans.length - 1];
            const isWithinWindow = EventUtil.isCurrentTimeWithinSession(
              firstPlan.start_time,
              lastPlan.end_time,
              sessionEndBufferTimeInMinutes,
            );
            return new UpdateSessionHoursStatus(isWithinWindow, {
              start_time: firstPlan.start_time,
              end_time: lastPlan.end_time,
            });
          }),
        );
      }),
    ),
  );

  ChangeShiftId$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ChangeShiftId>(ShiftsActionTypes.ChangeShiftId),
        switchMap((action) =>
          from(this.persistentStorageService.set('[SHIFT][USER_SETTING]', action.shiftId)).pipe(
            map(() => {
              console.log('[SHIFT][USER_SETTING] is update');
            }),
            catchError((error) => {
              console.log('[SHIFT][USER_SETTING] update failed');
              return of('Error', error);
            }),
          ),
        ),
      ),
    {
      dispatch: false,
    },
  );
}
